// src/components/CreateDealModal.js
// Shared modal component for creating new deals
// Used by Deals.js and all product-specific deal modals (Enterprise, Ransomware, DarkWeb, AI, TPRM, BusinessUnits)

import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import { useRegions } from "../contexts/RegionContext";
import { useVerticals } from "../contexts/VerticalsContext";
import Button from './Buttons';
import { getAllCountryOptions, getStateOptions } from "../utils/countries";
import { shouldShowRegionPicker, defaultRegionId, regionCountryCodes } from "../utils/regions";
import { useGoogleMapsLoader } from "../hooks/useGoogleMapsLoader";
import { useAddressAutocomplete } from "../hooks/useAddressAutocomplete";

const CreateDealModal = ({
    isOpen,
    onClose,
    onDealCreated,
    dealCategory = "Application",
    organizationId = null,
    title = "Create New Deal"
}) => {
    const apiCall = useApi();
    const { user, refreshUserRoles } = useAuth();
    const regions = useRegions();
    const { verticals } = useVerticals();

    // Get organization ID from props or localStorage
    const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
    const effectiveOrgId = organizationId || org?.organizationId || "";

    const [formData, setFormData] = useState({
        DealName: "",
        RegionId: String(org?.regionId || ""),
        VerticalId: "",
        SubVerticalId: "",
        Domain: "",
        DealCategory: dealCategory,
        OrganizationId: effectiveOrgId,
        DealOwner: "",
        DealStartDate: "",
        DealEndDate: "",
        DealCountry: "",
        DealAddress: "",
        DealCity: "",
        DealState: "",
        DealZip: "",
        DealPhoneNo: "",
        DealStatus: "",
        IsPrivate: false,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dateError, setDateError] = useState("");

    // Reset form each time the modal opens (component stays mounted, only isOpen toggles)
    useEffect(() => {
        if (isOpen) {
            setFormData({
                DealName: "",
                RegionId: String(org?.regionId || ""),
                VerticalId: "",
                SubVerticalId: "",
                Domain: "",
                DealCategory: dealCategory,
                OrganizationId: effectiveOrgId,
                DealOwner: "",
                DealStartDate: "",
                DealEndDate: "",
                DealCountry: "",
                DealAddress: "",
                DealCity: "",
                DealState: "",
                DealZip: "",
                DealPhoneNo: "",
                DealStatus: "",
                IsPrivate: false,
            });
            setDateError("");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // ── Google Maps ─────────────────────────────────────────────────────────
    const { isLoaded: googleIsLoaded, loadError: googleLoadError, isInactive: googleMapsInactive } = useGoogleMapsLoader();

    // Keep RegionId on a configured region: the org's region when configured, the only region
    // when there is one (the picker is then hidden), otherwise the user's choice.
    useEffect(() => {
        // Wait for the regions to load: an empty list would clear the org's prefilled region.
        if (!regions?.length) return;
        const id = defaultRegionId(regions, formData.RegionId);
        if (id !== formData.RegionId) {
            setFormData((prev) => ({ ...prev, RegionId: id }));
        }
    }, [regions, formData.RegionId]);

    const showRegionPicker = shouldShowRegionPicker(regions);
    const selectedRegion = regions.find(r => String(r.regionId) === String(formData.RegionId));

    // Memoized region codes — prevents infinite loop in useAddressAutocomplete.
    // Keyed on countryFilterKey (not the display name, which an operator may override).
    const regionCodes = useMemo(
        () => regionCountryCodes(selectedRegion),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedRegion?.countryFilterKey]
    );

    // Country dropdown options (all countries, not filtered by region)
    const countryOptions = getAllCountryOptions();

    // Field name adapter: useAddressAutocomplete uses organization* keys; form uses Deal* keys
    const ORG_TO_DEAL = {
        organizationAddress1: 'DealAddress',
        organizationCity:    'DealCity',
        organizationState:   'DealState',
        organizationZip:     'DealZip',
        organizationCountry: 'DealCountry',
    };

    const handleBatchChange = useCallback((updates) => {
        setFormData(prev => {
            const mapped = {};
            for (const [key, val] of Object.entries(updates)) {
                mapped[ORG_TO_DEAL[key] ?? key] = val;
            }
            return { ...prev, ...mapped };
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Address autocomplete hook
    const { suggestions, showSuggestions, setShowSuggestions, handleSuggestionSelect, zipLoading } =
        useAddressAutocomplete({
            isLoaded:      googleIsLoaded,
            addressValue:  formData.DealAddress,
            country:       formData.DealCountry,
            regionCodes,
            zip:           formData.DealZip,
            city:          formData.DealCity,
            state:         formData.DealState,
            onBatchChange: handleBatchChange,
        });

    // Click-outside closes address suggestions
    const addressWrapperRef = useRef(null);
    useEffect(() => {
        const close = (e) => {
            if (addressWrapperRef.current && !addressWrapperRef.current.contains(e.target))
                setShowSuggestions(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [setShowSuggestions]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const handleCountryChange = (e) => {
        setFormData(prev => ({ ...prev, DealCountry: e.target.value, DealState: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.DealStartDate && formData.DealEndDate && formData.DealEndDate <= formData.DealStartDate) {
            setDateError("End date must be after the start date.");
            return;
        }
        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                RegionId: formData.RegionId ? parseInt(formData.RegionId, 10) : null,
                VerticalId: formData.VerticalId ? parseInt(formData.VerticalId, 10) : null,
                SubVerticalId: formData.SubVerticalId ? parseInt(formData.SubVerticalId, 10) : null,
            };
            const res = await apiCall("POST", "/deals", payload);
            await refreshUserRoles();
            onDealCreated?.(res);
            onClose();
        } catch (error) {} finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50">
            <div className="bg-theme-secondary p-6 rounded-lg w-[500px] shadow-lg max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-semibold mb-4 text-content-primary">{title}</h2>

                <form onSubmit={handleSubmit} className="space-y-3">
                    {/* Private deal — first, so the visibility choice is made before the details */}
                    <div className="p-3 border border-border rounded-md bg-theme-primary/40">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.IsPrivate}
                                onChange={(e) => setFormData({ ...formData, IsPrivate: e.target.checked })}
                                className="h-4 w-4 mt-0.5 text-accent-cyan border-border rounded focus:ring-accent-cyan/50"
                            />
                            <span>
                                <span className="block text-sm font-medium text-content-secondary">Private deal</span>
                                <span className="block text-xs text-content-muted">
                                    Only you and people you explicitly share it with can see this deal — organization admins included.
                                </span>
                            </span>
                        </label>
                    </div>

                    <Input
                        label="Deal Name"
                        value={formData.DealName}
                        onChange={(e) => setFormData({ ...formData, DealName: e.target.value })}
                        required
                    />

                    {/* Data Residency Region (hidden when the installation has one region) */}
                    {showRegionPicker && (
                    <div>
                        <label className="block text-sm font-medium text-content-secondary mb-1">
                            Data Residency Region <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={formData.RegionId}
                            onChange={(e) => setFormData({ ...formData, RegionId: e.target.value })}
                            className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                            required
                        >
                            <option value="">-- Select Region --</option>
                            {regions.map((r) => (
                                <option key={r.regionId} value={r.regionId}>{r.regionName}</option>
                            ))}
                        </select>
                    </div>
                    )}

                    {/* Vertical */}
                    <div>
                        <label className="block text-sm font-medium text-content-secondary mb-1">Vertical <span className="text-red-500">*</span></label>
                        <select
                            value={formData.VerticalId}
                            onChange={(e) => setFormData({ ...formData, VerticalId: e.target.value, SubVerticalId: "" })}
                            className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                            required
                        >
                            <option value="">-- Select Vertical --</option>
                            {verticals.map((v) => (
                                <option key={v.verticalId} value={v.verticalId}>{v.verticalName}</option>
                            ))}
                        </select>
                    </div>

                    {/* SubVertical */}
                    <div>
                        <label className="block text-sm font-medium text-content-secondary mb-1">Sub-Vertical <span className="text-red-500">*</span></label>
                        <select
                            value={formData.SubVerticalId}
                            onChange={(e) => setFormData({ ...formData, SubVerticalId: e.target.value })}
                            className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan disabled:opacity-50"
                            required
                            disabled={!formData.VerticalId}
                        >
                            <option value="">-- Select Sub-Vertical --</option>
                            {verticals
                                .find((v) => v.verticalId === parseInt(formData.VerticalId))?.subVerticals
                                ?.map((sv) => (
                                    <option key={sv.subVerticalId} value={sv.subVerticalId}>{sv.subVerticalName}</option>
                                ))}
                        </select>
                    </div>

                    <Input
                        label="Domain"
                        value={formData.Domain}
                        onChange={(e) => setFormData({ ...formData, Domain: e.target.value })}
                        placeholder="e.g., example.com"
                    />
                    {dealCategory === "DarkWeb" && (
                        <p className="text-sm text-gray-500 -mt-2">
                            Please verify or update the domain name related to the deal, or leave blank if none. We will scan the Dark Web for any exposures related to the deal.
                        </p>
                    )}

                    <Input
                        label="Deal Owner"
                        value={formData.DealOwner}
                        onChange={(e) => setFormData({ ...formData, DealOwner: e.target.value })}
                    />
                    <Input
                        label="Start Date"
                        type="date"
                        value={formData.DealStartDate}
                        onChange={(e) => {
                            const newStart = e.target.value;
                            setDateError("");
                            setFormData(prev => ({
                                ...prev,
                                DealStartDate: newStart,
                                // Clear end date if it's now on or before the new start date
                                DealEndDate: prev.DealEndDate && prev.DealEndDate <= newStart ? "" : prev.DealEndDate,
                            }));
                        }}
                        required
                    />
                    <Input
                        label="End Date"
                        type="date"
                        value={formData.DealEndDate}
                        min={formData.DealStartDate || undefined}
                        onChange={(e) => { setDateError(""); setFormData({ ...formData, DealEndDate: e.target.value }); }}
                        required
                    />
                    {dateError && <p className="text-red-500 text-sm -mt-2">{dateError}</p>}

                    {/* Country */}
                    <div>
                        <label className="block text-sm font-medium text-content-secondary mb-1">Country</label>
                        <select
                            value={formData.DealCountry}
                            onChange={handleCountryChange}
                            className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                        >
                            {countryOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Address with Google Places autocomplete */}
                    <div ref={addressWrapperRef} className="relative">
                        <Input
                            label="Address"
                            value={formData.DealAddress}
                            onChange={(e) => setFormData({ ...formData, DealAddress: e.target.value })}
                            autoComplete="off"
                            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                            placeholder={googleLoadError || googleMapsInactive ? 'Enter street address' : 'Start typing to search address…'}
                        />

                        {showSuggestions && suggestions.length > 0 && (
                            <ul
                                className="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.10)] overflow-hidden"
                                style={{ top: 'calc(100% - 1rem)', marginTop: '2px' }}
                            >
                                {suggestions.map((s, i) => (
                                    <li
                                        key={s.placePrediction?.placeId || i}
                                        className="border-b border-gray-100 last:border-b-0"
                                    >
                                        <button
                                            type="button"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleSuggestionSelect(s)}
                                            className="w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                                        >
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {s.placePrediction?.mainText?.text}
                                            </p>
                                            {s.placePrediction?.secondaryText?.text && (
                                                <p className="text-xs text-gray-500 truncate mt-0.5">
                                                    {s.placePrediction.secondaryText.text}
                                                </p>
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {googleLoadError && (
                            <p className="text-xs text-gray-400 mt-1">
                                Address autocomplete unavailable — please enter manually.
                            </p>
                        )}
                    </div>

                    {/* ZIP | State / Province | City (matches OrganizationStep layout) */}
                    <div className="grid grid-cols-3 gap-2">
                        {/* ZIP / Postal Code */}
                        <div>
                            <Input
                                label="Postal Code"
                                value={formData.DealZip}
                                onChange={(e) => setFormData({ ...formData, DealZip: e.target.value })}
                            />
                            {zipLoading && (
                                <p className="text-xs text-gray-400 mt-1 animate-pulse">Looking up…</p>
                            )}
                        </div>

                        {/* State — dropdown for US, text input for all others */}
                        {formData.DealCountry === 'US' ? (
                            <div>
                                <label className="block text-sm font-medium text-content-secondary mb-1">State / Province</label>
                                <select
                                    value={formData.DealState}
                                    onChange={(e) => setFormData({ ...formData, DealState: e.target.value })}
                                    className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                                >
                                    {getStateOptions().map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <Input
                                label="State / Province"
                                value={formData.DealState}
                                onChange={(e) => setFormData({ ...formData, DealState: e.target.value })}
                            />
                        )}

                        {/* City */}
                        <Input
                            label="City"
                            value={formData.DealCity}
                            onChange={(e) => setFormData({ ...formData, DealCity: e.target.value })}
                        />
                    </div>

                    <Input
                        label="Phone No"
                        value={formData.DealPhoneNo}
                        onChange={(e) => setFormData({ ...formData, DealPhoneNo: e.target.value })}
                    />

                    {/* Status */}
                    <div>
                        <label className="block text-sm font-medium text-content-secondary mb-1">Status <span className="text-red-500">*</span></label>
                        <select
                            value={formData.DealStatus}
                            onChange={(e) => setFormData({ ...formData, DealStatus: e.target.value })}
                            className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                            required
                        >
                            <option value="">-- Select Status --</option>
                            <option value="Active">Active</option>
                            <option value="Pending">Pending</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                            <option value="On Hold">On Hold</option>
                            <option value="Cancelled">Cancelled</option>
                        </select>
                    </div>

                    {/* Created By — read-only, shows logged-in user */}
                    <div>
                        <label className="block text-sm font-medium text-content-secondary mb-1">Created By</label>
                        <div className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-muted cursor-default select-none">
                            {user?.userName || user?.email || '—'}
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 mt-4">
                        <Button type="button" variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Creating...
                                </>
                            ) : (
                                "Create Deal"
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Simple Input helper
const Input = ({ label, type = "text", ...props }) => (
    <div>
        <label className="block text-sm font-medium text-content-secondary mb-1">
            {label}{props.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
            type={type}
            {...props}
            className="w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
        />
    </div>
);

export default CreateDealModal;
